import { fetchWithAdmin } from './fetchHelper';

/** One rendered marketing file — backend `admin/marketing/marketingAssets.ts`. */
export interface MarketingAsset {
  /** Inside the series: `ep01-normal-force/reel/ep01-normal-force-reel.mp4`. */
  path: string;
  name: string;
  /** `01`, or null for the shared files in `common/`. */
  episode: string | null;
  format: 'feed' | 'feed34' | 'story' | 'reel' | 'short';
  kind: 'challenge' | 'challenge-title' | 'answer-post' | 'answer-ad' | 'answer' | 'reel' | 'reel-cover' | 'short' | 'more' | 'other';
  media: 'video' | 'image';
  size: number;
  contentType?: string;
  lastModified?: string;
  /** Read-only signed URL; downloads as `name`. Valid until `urlsValidUntil`. */
  url: string;
}

export interface MarketingAssetList {
  series: string;
  container: string;
  files: MarketingAsset[];
  urlsValidUntil: string;
}

/** Kinds of post the backend publishes. Stories and ads stay manual. */
export type PublishKit = 'reel' | 'carousel';

/** The Instagram connection — backend `admin/marketing/instagram/instagramEndpoints.ts`. */
export interface InstagramStatus {
  connected: boolean;
  /** App settings that are not set (INSTAGRAM_ACCESS_TOKEN, INSTAGRAM_USER_ID). */
  missing?: string[];
  /** Set but refused by Meta: an expired or revoked token, a wrong id. */
  error?: string;
  account?: { id: string; username: string; name?: string; profilePictureUrl?: string };
  /** Posts used in Meta's rolling 24-hour window. */
  limit?: { used: number; total: number } | null;
  /** The account's recent What's Wrong? posts, by episode id. */
  episodePosts?: Record<string, { id: string; permalink?: string; timestamp?: string; type?: string }[]>;
}

export interface KitPlan {
  kit: PublishKit;
  episode: string;
  items: { path: string; media: 'video' | 'image'; role: string }[];
  cover?: { path: string; media: 'video' | 'image'; role: string };
}

/** A track from Instagram's music library (the Audio API). */
export interface MusicTrack {
  audioId: string;
  title: string;
  artist: string;
  durationMs: number;
  /** Meta's flag: may also be used if the post is boosted as an ad. */
  adsEligible: boolean;
  /** A short-lived preview. */
  previewUrl?: string;
  artworkUrl?: string;
}

/** A track offered first: on the series' own list, or used on an earlier reel. */
export interface SuggestedTrack extends Partial<MusicTrack> {
  title: string;
  artist: string;
  source: 'series' | 'used';
  /** False when Instagram doesn't offer it to apps today: shown, not pickable. */
  available: boolean;
}

export interface PublishProgress {
  jobId: string;
  status: 'queued' | 'running' | 'completed' | 'failed' | 'dead';
  episode?: string;
  kit?: PublishKit;
  result?: { mediaId: string; permalink?: string; containerIds: string[] };
  error?: string;
  queuedAt: string;
  completedAt?: string;
}

export const marketingApi = {
  assets: (series = 'whats-wrong') =>
    fetchWithAdmin<MarketingAssetList>(`/console/marketing/assets?series=${encodeURIComponent(series)}`),
  instagramStatus: () => fetchWithAdmin<InstagramStatus>('/console/marketing/instagram'),
  instagramPlan: (episode: string, kit: PublishKit, series = 'whats-wrong') =>
    fetchWithAdmin<KitPlan>(`/console/marketing/instagram/plan?series=${encodeURIComponent(series)}&episode=${episode}&kit=${kit}`),
  instagramSuggestedMusic: (series = 'whats-wrong') =>
    fetchWithAdmin<{ tracks: SuggestedTrack[] }>(`/console/marketing/instagram/music/suggested?series=${encodeURIComponent(series)}`),
  instagramMusic: (q: string) =>
    fetchWithAdmin<{ tracks: MusicTrack[] }>(`/console/marketing/instagram/music?q=${encodeURIComponent(q)}`),
  publishToInstagram: (body: {
    series: string; episode: string; kit: PublishKit; caption: string; username: string; requestId: string;
    audio?: { audioId: string; title?: string; artist?: string };
    /** The caption becomes the poll's question. */
    poll?: { options: string[]; keepOpen: boolean };
  }) =>
    fetchWithAdmin<{ jobId: string; status: string; plan: KitPlan }>('/console/marketing/instagram/publish', { method: 'POST', body: JSON.stringify(body) }),
  instagramPublish: (jobId: string) =>
    fetchWithAdmin<PublishProgress>(`/console/marketing/instagram/publishes/${encodeURIComponent(jobId)}`),
};
